<template>
  <splide
    ref="splide"
    class="hero-banner critical-component-clear"
    :has-track="false"
    :options="config"
    @splide:drag="handleSplideIsDragging"
    @splide:dragged="handleSplideDragged"
    @splide:move="handleSplideMove"
    @splide:moved="handleSplideMoved"
  >
    <splide-track>
      <splide-slide
        v-for="(banner, index) of bannerData"
        :key="`banner-${index}`"
      >
        <video-player
          v-if="hasVideo(banner)"
          :key="isMobile"
          class="hero-banner__video"
          controls-alignment="right"
          :cta-url="isMobile && banner.ctaUrl"
          :image="getVideoPreviewImage(banner)"
          :is-active="index === currentIndex"
          :lazyload="banner.useLazyload"
          :use-overlay-button="false"
          :video="getVideo(banner)"
        />

        <component
          :is="banner.ctaUrl ? 'a' : 'div'"
          class="hero-banner__banner"
          :class="{
            'hero-banner__banner--is-dragged': isDragging,
            'hero-banner__banner--has-video': hasVideo(banner)
          }"
          :href="banner.ctaUrl"
        >
          <div class="hero-banner__media">
            <art-direction
              v-if="!hasVideo(banner)"
              :image="banner.imageMobile"
              :image-desktop="banner.imageDesktop"
              :lazyload="banner.useLazyload"
              :ratio="6/7"
              :ratio-desktop="2/1"
            />
          </div>

          <div
            class="hero-banner__brand-logo"
            :class="banner.logoClass"
            role="presentation"
          >
            <icon-brand-logo />
          </div>

          <div
            v-if="showBannerContent(banner)"
            class="grid container hero-banner__content-container"
          >
            <div class="hero-banner__content col xs4 l3">
              <h2
                class="text-heading-m hero-banner__title"
                v-text="banner.title"
              />

              <p
                class="hero-banner__copy"
                v-text="banner.copy"
              />

              <btn
                v-if="banner.ctaLabel && banner.ctaUrl"
                custom-element="span"
                class="hero-banner__cta"
                :label="banner.ctaLabel"
                modifiers="secondary"
                :url="banner.ctaUrl"
              />
            </div>
          </div>
        </component>
      </splide-slide>
    </splide-track>

    <div
      v-if="bannerData.length > 1"
      class="grid container hero-banner__controls"
    >
      <ul class="col xs4-4 l3-3 hero-banner__pagination">
        <li
          v-for="(banner, index) of bannerData"
          :key="`banner-${index}`"
        >
          <button
            class="hero-banner__pagination-button splide__pagination__page"
            type="button"
            :aria-current="Boolean(currentIndex === index)"
            @click.prevent="handlePaginationClick(index)"
          >
            <span
              class="visually-hidden"
              v-text="$string(
                'accessibility.carousel.go_to_slide',
                { replace: { slide: index + 1 } }
              )"
            />

            <span
              v-if="currentIndex === index"
              class="hero-banner__pagination-icon"
            >
              <icon-cross v-if="index % 2 === 0" />
              <icon-cross2 v-else />
            </span>
          </button>
        </li>
      </ul>

      <div
        class="col xs4-4 l3-3 splide__toggle-wrapper"
        :class="{'splide__toggle-wrapper--hidden': hideSplideToggle}"
      >
        <button
          class="splide__toggle button button--text"
          type="button"
        >
          <span class="splide__toggle__play">
            <icon-play />
          </span>

          <span class="splide__toggle__pause">
            <icon-pause />
          </span>
        </button>
      </div>
    </div>
  </splide>
</template>

<script>

/**
 * Vue: Hero banner (hero-banner)
 * -----------------------------------------------------------------------------
 * Full width carousel that supports images and videos.
 *
 * @param {Array} bannerData - Formatted banner data from Liquid.
 * @param {Object} liquid - Liquid section settings.
 *
 */
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide'
import { mapState } from 'vuex'

import IconBrandLogo from '~icons/general/brand-logo.svg'
import IconCross2 from '~icons/general/cross-2.svg'
import IconCross from '~icons/general/cross.svg'
import IconPause from '~icons/general/pause.svg'
import IconPlay from '~icons/general/play.svg'

import VideoPlayer from '~async/video-player/video-player'
import Btn from '~global/btn/btn'
import ArtDirection from '~global/images/art-direction'

export default {
  name: 'HeroBanner',

  components: {
    ArtDirection,
    Btn,
    IconBrandLogo,
    IconCross,
    IconCross2,
    IconPause,
    IconPlay,
    Splide,
    SplideSlide,
    SplideTrack,
    VideoPlayer,
  },

  props: {
    bannerData: {
      type: Array,
      default: () => [],
      required: true,
    },
    liquid: {
      type: Object,
      default: () => ({
        autoplayInterval: 5000,
        rotationSpeed: 300,
      }),
    },
  },

  data() {
    return {
      config: {
        arrows: false,
        autoplay: true,
        drag: true,
        flickPower: 200,
        flickMaxPages: 1,
        interval: this.liquid.autoplayInterval,
        mediaQuery: 'min',
        pagination: false,
        speed: this.liquid.rotationSpeed,
        start: this.currentIndex || 0,
        type: this.bannerData.length === 1 ? 'slide' : 'loop',
      },
      currentIndex: 0,
      hideSplideToggle: false,
      isDragging: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),
  },

  methods: {

    /**
     * Returns the appropriate mobile/desktop video source depending on
     * resolution.
     * - Fallbacks set in Liquid.
     * @param {Object} banner - Individual banner data.
     * @returns {String}
     */
    getVideo(banner) {
      return this.isMobile
        ? this.getVideoSource(banner.videoMobile.video.sources)
        : this.getVideoSource(banner.videoDesktop.video.sources)
    },

    /**
     * Returns the appropriate mobile/desktop preview image.
     * - Fallbacks are set in Liquid.
     * @param {Object} banner - Individual banner data.
     * @returns {Object}
     */
    getVideoPreviewImage(banner) {
      return this.isMobile
        ? banner.videoMobile.preview_image
        : banner.videoMobile.preview_image
    },

    /**
     * Get video MP4 source object.
     * @param {Array} sources - Video sources.
     * @returns {Object}
     */
    getVideoSource(sources) {
      return sources.find((source) => source.format === 'mp4')
    },

    /**
     * Check if at least one video was uploaded.
     * @param {Object} banner - Individual banner data.
     * @returns {Boolean}
     */
    hasVideo(banner) {
      return banner.videoDesktop || banner.videoMobile
    },

    /**
     * Handle Splide move event.
     */
    handleSplideMove() {
      this.hideSplideToggle = true
    },

    /**
     * Handle Splide moved event.
     * - Updates current slide index to share with modal.
     * @param {Object} splide - Splide instance.
     * @param {Number} newIndex - New slide index.
     * @param {Number} destIndex - Destination slide index.
     */
    handleSplideMoved(_, newIndex) {
      this.currentIndex = newIndex
      this.hideSplideToggle = false
    },

    /**
     * Events for clicking on a paginated dot.
     * @param {Integer|String} index - slide to navigate to.
     * Accepts slide index or relative string (i.e. '>2').
     */
    handlePaginationClick(index = 0) {
      this.$refs.splide.go(index)
    },

    /**
     * Handle event when a Splide slide is being dragged by the user.
     */
    handleSplideIsDragging() {
      this.setIsDragging(true)
    },

    /**
     * Handle event when a user stops dragging a Splide slide.
     */
    handleSplideDragged() {
      this.setIsDragging(false)
    },

    /**
     * Update value of `isDragging` variable.
     * @param {Boolean} isDragging - If the slide is actively being dragged.
     */
    setIsDragging(isDragging) {
      this.isDragging = isDragging
    },

    /**
     * Show Banner content card.
     * @param {Object} banner - Individual banner data.
     * @returns {Boolean}
     */
    showBannerContent(banner) {
      const hasContent = banner.title ||
        banner.copy ||
        (banner.ctaLabel && banner.ctaUrl)

      return this.bannerData.length > 1 || hasContent
    },
  },
}
</script>

<style lang="scss">
@import '@/components/splide';
@import './hero-banner';
</style>
